import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { videoCategories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { searchVideos, sortVideos, paginate, type SortKey } from "@/utils/videoQuery";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 9;

const SORTS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "时间最新" },
  { key: "likes", label: "最多点赞" },
  { key: "comments", label: "最多评论" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("query") ?? "";
  const [input, setInput] = useState(query);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 樱桃视频` : "搜索视频 - 樱桃视频",
    query
      ? `樱桃视频为你找到与“${query}”相关的视频内容。`
      : "在樱桃视频搜索你感兴趣的短视频、网络电影与娱乐综艺。"
  );

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  const results = useMemo(() => {
    const matched = searchVideos(videos, query);
    return sortVideos(matched, sort);
  }, [query, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = paginate(results, safePage, PAGE_SIZE);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = input.trim();
    if (!keyword) {
      setSearchParams({});
      return;
    }
    setSearchParams({ query: keyword });
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="mt-5 max-w-2xl">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">搜索视频</h1>
        <form onSubmit={handleSubmit} className="mt-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                type="text"
                placeholder="输入关键词，如：美食、科幻、真人秀"
                className="w-full rounded-md bg-background-100 border border-background-200 pl-10 pr-4 py-3 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空"
                  onClick={() => setInput("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-circle-fill"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </div>
        </form>
      </div>

      {query ? (
        <>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              找到 <span className="font-semibold text-foreground-900">{results.length}</span> 个与
              <span className="font-semibold text-primary-600"> “{query}” </span>
              相关的视频
            </p>
            {results.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                {SORTS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      setSort(item.key);
                      setPage(1);
                    }}
                    className={`rounded-full px-3 py-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      sort === item.key
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {results.length === 0 ? (
            <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 px-6 text-center">
              <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </div>
              <p className="mt-4 text-base font-heading font-semibold text-foreground-700">
                未找到与“{query}”相关的内容
              </p>
              <p className="mt-2 text-sm text-foreground-500">试试其他关键词，或浏览下面的热门分类</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {videoCategories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`/category/${category.slug}`}
                    className="rounded-full bg-background-50 border border-secondary-300 px-4 py-2 text-sm text-secondary-900 hover:bg-secondary-100 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-7">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination current={safePage} total={totalPages} onChange={setPage} />
            </>
          )}
        </>
      ) : (
        <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 px-6 text-center">
          <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-search-line text-3xl"></i>
          </div>
          <p className="mt-4 text-base font-heading font-semibold text-foreground-700">
            输入关键词，发现你喜欢的视频
          </p>
          <p className="mt-2 text-sm text-foreground-500">也可以直接从热门分类开始浏览</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {videoCategories.map((category) => (
              <Link
                key={category.slug}
                to={`/category/${category.slug}`}
                className="rounded-full bg-background-50 border border-secondary-300 px-4 py-2 text-sm text-secondary-900 hover:bg-secondary-100 transition-colors whitespace-nowrap"
              >
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}